/**
 * URL utilities for Deals page
 */

import { COUNTRY_LANGUAGES } from './constants';

/**
 * Build IKEA product URL for a specific country
 */
export function buildProductUrl(
  productCode: string,
  country: string
): string {
  const lang = COUNTRY_LANGUAGES[country] || 'en';
  const countryLower = country.toLowerCase();
  const codeWithoutDots = productCode.replace(/\./g, '');

  return `https://www.ikea.com/${countryLower}/${lang}/p/-${codeWithoutDots}/`;
}

/**
 * Build IKEA As-Is store page URL
 */
export function buildAsIsStoreUrl(
  storeId: string,
  country: string
): string {
  const lang = COUNTRY_LANGUAGES[country] || 'en';
  const countryLower = country.toLowerCase();

  // IKEA's As-Is URL format varies by country
  // This is a generic format - may need adjustment for specific countries
  return `https://www.ikea.com/${countryLower}/${lang}/stores/${storeId}/as-is/`;
}

/**
 * Build direct As-Is product URL if source_url is available
 */
export function buildDirectAsIsUrl(
  sourceUrl: string | undefined,
  productCode: string,
  country: string
): string {
  // If we have a direct source URL from the scraper, use it
  if (sourceUrl) {
    return sourceUrl;
  }

  // Fall back to regular product page
  return buildProductUrl(productCode, country);
}

/**
 * Open product page in new tab
 */
export function openProductPage(
  productCode: string,
  country: string,
  sourceUrl?: string
): void {
  const url = sourceUrl || buildProductUrl(productCode, country);
  window.open(url, '_blank');
}

/**
 * Get country flag emoji
 */
export function getCountryFlag(countryCode: string): string {
  // Convert country code to flag emoji
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
}

/**
 * Get country name from code
 */
export function getCountryName(countryCode: string): string {
  const countryNames: Record<string, string> = {
    US: 'United States',
    CA: 'Canada',
    AU: 'Australia',
    GB: 'United Kingdom',
    DE: 'Germany',
    FR: 'France',
    ES: 'Spain',
    IT: 'Italy',
    NL: 'Netherlands',
    SE: 'Sweden',
    NO: 'Norway',
    DK: 'Denmark',
    FI: 'Finland',
    PL: 'Poland',
    AT: 'Austria',
    CH: 'Switzerland',
    BE: 'Belgium',
    IE: 'Ireland',
    PT: 'Portugal',
    CZ: 'Czech Republic',
  };

  return countryNames[countryCode] || countryCode;
}

/**
 * Parse store ID from URL or stored value
 */
export function parseStoredStore(storedValue: string): {
  storeId: string | null;
  storeName: string | null;
  country: string | null;
} {
  try {
    const parsed = JSON.parse(storedValue);
    return {
      storeId: parsed.storeId || null,
      storeName: parsed.storeName || null,
      country: parsed.country || null,
    };
  } catch {
    return {
      storeId: null,
      storeName: null,
      country: null,
    };
  }
}

/**
 * Build shareable deals URL with filters
 */
export function buildShareableUrl(filters: {
  category?: string;
  country?: string;
  store?: string;
  search?: string;
  sort?: string;
}): string {
  const params = new URLSearchParams();

  if (filters.category && filters.category !== 'all') {
    params.set('category', filters.category);
  }
  if (filters.country) {
    params.set('country', filters.country);
  }
  if (filters.store) {
    params.set('store', filters.store);
  }
  if (filters.search) {
    params.set('search', filters.search);
  }
  if (filters.sort && filters.sort !== 'newest') {
    params.set('sort', filters.sort);
  }

  const queryString = params.toString();
  return queryString ? `/deals?${queryString}` : '/deals';
}
